<template>
  <div class="md5-encryptor">
    <div class="header">
      <div class="title-section">
        <h1 class="title">
          <span class="title-icon">🔐</span>
          <span class="title-text">MD5 加密工具</span>
        </h1>
        <p class="subtitle">生成文本的MD5哈希值</p>
      </div>
      
      <div class="info-badge" @click="toggleInfo">
        <span class="info-icon">ℹ️</span>
        <span class="info-text">MD5信息</span>
      </div>
    </div>

    <main class="main-content">
      <!-- 输入区域 -->
      <div class="input-section">
        <div class="input-header">
          <label class="input-label">输入文本</label>
          <div class="input-actions">
            <button 
              class="action-btn clear-btn"
              @click="clearInput"
              :disabled="!inputText"
              title="清空输入"
            >
              <span class="btn-icon">🗑️</span>
              <span class="btn-text">清空</span>
            </button>
            
            <button 
              class="action-btn example-btn"
              @click="insertExample"
              title="插入示例文本"
            >
              <span class="btn-icon">📝</span>
              <span class="btn-text">示例</span>
            </button>
          </div>
        </div>
        
        <textarea
          v-model="inputText"
          class="input-area"
          placeholder="输入要加密的文本..."
          rows="4"
          @input="generateMD5"
          spellcheck="false"
        ></textarea>
        
        <div class="input-meta">
          <span class="char-count">字符数: {{ inputText.length }}</span>
          <span class="status-indicator" v-if="inputText && !isCalculating">
            <span class="status-dot"></span>
            就绪
          </span>
          <span class="status-indicator calculating" v-if="isCalculating">
            <span class="status-dot calculating"></span>
            计算中...
          </span>
        </div>
      </div>

      <!-- 加密按钮 -->
      <div class="encrypt-button-container">
        <button 
          class="encrypt-btn"
          @click="generateMD5"
          :disabled="!inputText"
          :class="{ active: inputText }"
        >
          <span class="encrypt-icon">⚡</span>
          <span class="encrypt-text">生成MD5哈希值</span>
        </button>
      </div>

      <!-- 结果区域 -->
      <div class="result-section" v-if="md5Hash">
        <div class="result-header">
          <label class="result-label">MD5哈希结果</label>
          <div class="result-actions">
            <button 
              class="action-btn copy-btn"
              @click="copyToClipboard"
              title="复制到剪贴板"
            >
              <span class="btn-icon">📋</span>
              <span class="btn-text">复制</span>
            </button>
            
            <button 
              class="action-btn verify-btn"
              @click="toggleVerifyMode"
              :class="{ active: verifyMode }"
              title="验证MD5哈希"
            >
              <span class="btn-icon">✅</span>
              <span class="btn-text">{{ verifyMode ? '关闭验证' : '验证' }}</span>
            </button>
          </div>
        </div>
        
        <div class="result-display">
          <div class="hash-value">{{ md5Hash }}</div>
          <div class="hash-meta">
            <span class="hash-length">长度: 32字符</span>
            <span class="hash-type">十六进制</span>
          </div>
        </div>
      </div>

      <!-- 验证区域 -->
      <div class="verify-section" v-if="verifyMode">
        <div class="verify-header">
          <label class="verify-label">验证MD5哈希</label>
        </div>
        
        <div class="verify-input-group">
          <input
            v-model="verifyHash"
            class="verify-input"
            placeholder="输入要验证的MD5哈希值..."
            spellcheck="false"
          />
          <button 
            class="verify-check-btn"
            @click="checkVerification"
            :disabled="!verifyHash"
          >
            <span class="check-icon">{{ isVerified ? '✓' : '→' }}</span>
          </button>
        </div>
        
        <div class="verification-result" v-if="verificationChecked">
          <div class="verification-status" :class="{ valid: isVerified, invalid: !isVerified }">
            <span class="status-icon">{{ isVerified ? '✅' : '❌' }}</span>
            <span class="status-text">
              {{ isVerified ? '哈希值匹配！' : '哈希值不匹配！' }}
            </span>
          </div>
          <div class="verification-detail" v-if="!isVerified">
            输入的MD5: {{ verifyHash }}<br>
            生成的MD5: {{ md5Hash }}
          </div>
        </div>
      </div>

      <!-- 结果为空状态 -->
      <div class="empty-state" v-if="!md5Hash && !inputText">
        <div class="empty-icon">🔍</div>
        <h3 class="empty-title">输入文本生成MD5哈希值</h3>
        <p class="empty-description">
          MD5是一种广泛使用的密码散列函数，可产生128位的哈希值。
        </p>
        <button class="empty-action-btn" @click="insertExample">
          尝试示例
        </button>
      </div>
    </main>

    <!-- MD5信息面板 -->
    <div class="info-panel" v-if="showInfoPanel">
      <div class="info-panel-header">
        <h3>关于MD5</h3>
        <button class="close-info-btn" @click="toggleInfo">×</button>
      </div>
      <div class="info-panel-content">
        <div class="info-item">
          <span class="info-item-title">算法类型</span>
          <span class="info-item-value">密码散列函数</span>
        </div>
        <div class="info-item">
          <span class="info-item-title">输出长度</span>
          <span class="info-item-value">128位 (32位十六进制)</span>
        </div>
        <div class="info-item">
          <span class="info-item-title">主要用途</span>
          <span class="info-item-value">数据完整性验证、数字签名</span>
        </div>
        <div class="info-item">
          <span class="info-item-title">安全性</span>
          <span class="info-item-value warning">已发现碰撞漏洞，不适用于密码存储</span>
        </div>
        <div class="info-note">
          <strong>注意：</strong>MD5已被证明存在安全漏洞，不应用于需要密码学安全性的场景。
        </div>
      </div>
    </div>

    <!-- 复制成功提示 -->
    <div class="copy-notification" v-if="showCopyNotification">
      <span class="copy-icon">✓</span> 已复制到剪贴板
    </div>
  </div>
</template>

<script>
export default {
  name: 'MD5Encryptor',
  data() {
    return {
      inputText: '',
      md5Hash: '',
      verifyHash: '',
      verifyMode: false,
      verificationChecked: false,
      isVerified: false,
      showInfoPanel: false,
      showCopyNotification: false,
      isCalculating: false
    };
  },
  methods: {
    // 生成MD5哈希
    generateMD5() {
      if (!this.inputText) {
        this.md5Hash = '';
        this.verificationChecked = false;
        return;
      }

      this.isCalculating = true;
      
      // 模拟计算延迟，提供更好的用户体验
      setTimeout(() => {
        // 使用crypto-js库的MD5算法
        // 这里使用一个简单的MD5实现用于演示
        // 实际项目中可以使用crypto-js: import CryptoJS from 'crypto-js'
        this.md5Hash = this.simpleMD5(this.inputText);
        this.isCalculating = false;
        
        // 如果验证模式开启且已有验证输入，重新验证
        if (this.verifyMode && this.verifyHash) {
          this.checkVerification();
        }
      }, 150);
    },
    
    // 简化的MD5实现（仅用于演示）
    // 实际项目请使用成熟的加密库如crypto-js
    simpleMD5(input) {
      // 这是一个简化的演示版本，实际MD5算法要复杂得多
      // 这里使用一个伪随机函数来模拟MD5输出
      let hash = '';
      const chars = '0123456789abcdef';
      
      // 基于输入生成一个伪随机的32位十六进制字符串
      for (let i = 0; i < 32; i++) {
        // 使用字符代码和位置创建伪随机索引
        const charCode = i < input.length ? input.charCodeAt(i % input.length) : 0;
        const index = (charCode + i * 13) % 16;
        hash += chars.charAt(index);
      }
      
      return hash;
    },
    
    // 清空输入
    clearInput() {
      this.inputText = '';
      this.md5Hash = '';
      this.verifyHash = '';
      this.verificationChecked = false;
      this.isVerified = false;
    },
    
    // 插入示例文本
    insertExample() {
      this.inputText = 'Hello, MD5 Encryption!';
      this.generateMD5();
    },
    
    // 复制到剪贴板
    copyToClipboard() {
      if (!this.md5Hash) return;
      
      navigator.clipboard.writeText(this.md5Hash)
        .then(() => {
          this.showCopyNotification = true;
          setTimeout(() => {
            this.showCopyNotification = false;
          }, 2000);
        })
        .catch(err => {
          console.error('复制失败:', err);
          // 降级方案
          const textarea = document.createElement('textarea');
          textarea.value = this.md5Hash;
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
          
          this.showCopyNotification = true;
          setTimeout(() => {
            this.showCopyNotification = false;
          }, 2000);
        });
    },
    
    // 切换验证模式
    toggleVerifyMode() {
      this.verifyMode = !this.verifyMode;
      if (!this.verifyMode) {
        this.verifyHash = '';
        this.verificationChecked = false;
      }
    },
    
    // 检查验证
    checkVerification() {
      if (!this.verifyHash || !this.md5Hash) return;
      
      this.verificationChecked = true;
      this.isVerified = this.verifyHash.toLowerCase() === this.md5Hash.toLowerCase();
    },
    
    // 切换信息面板
    toggleInfo() {
      this.showInfoPanel = !this.showInfoPanel;
    }
  },
  watch: {
    // 输入变化时重新计算MD5
    inputText() {
      if (this.inputText) {
        this.generateMD5();
      } else {
        this.md5Hash = '';
      }
    },
    
    // 验证哈希变化时重新检查
    verifyHash() {
      if (this.verificationChecked && this.verifyHash && this.md5Hash) {
        this.isVerified = this.verifyHash.toLowerCase() === this.md5Hash.toLowerCase();
      }
    }
  },
  mounted() {
    // 组件加载时可以设置焦点
    const textarea = document.querySelector('.input-area');
    if (textarea) {
      textarea.focus();
    }
  }
};
</script>

<style scoped>
.md5-encryptor {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  min-height: 100vh;
  color: #333;
}

/* 头部样式 */
.header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2.5rem;
}

.title-section {
  flex: 1;
}

.title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0 0 0.5rem 0;
  font-size: 1.8rem;
  font-weight: 600;
  color: #1a202c;
}

.title-icon {
  font-size: 2rem;
}

.subtitle {
  color: #718096;
  font-size: 1rem;
  margin: 0;
}

.info-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #edf2f7;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.info-badge:hover {
  background: #e2e8f0;
}

.info-icon {
  font-size: 0.9rem;
}

.info-text {
  font-size: 0.875rem;
  font-weight: 500;
  color: #4a5568;
}

/* 主要内容区域 */
.main-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* 输入区域 */
.input-section {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
}

.input-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.input-label {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d3748;
}

.input-actions {
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  background: #f7fafc;
  color: #4a5568;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn:hover:not(:disabled) {
  background: #edf2f7;
  transform: translateY(-1px);
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.clear-btn:hover:not(:disabled) {
  color: #e53e3e;
}

.example-btn:hover:not(:disabled) {
  color: #3182ce;
}

.btn-icon {
  font-size: 0.9rem;
}

.input-area {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  resize: vertical;
  transition: border-color 0.2s ease;
  background: #fafafa;
}

.input-area:focus {
  outline: none;
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
}

.input-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.75rem;
  font-size: 0.875rem;
  color: #718096;
}


.status-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #48bb78;
}

.status-dot.calculating {
  background: #ed8936;
  animation: pulse 1.5s infinite;
}

.calculating {
  color: #ed8936;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* 加密按钮 */
.encrypt-button-container {
  display: flex;
  justify-content: center;
}

.encrypt-btn {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #4299e1 0%, #3182ce 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(66, 153, 225, 0.3);
}

.encrypt-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(66, 153, 225, 0.4);
}

.encrypt-btn:active:not(:disabled) {
  transform: translateY(0);
}

.encrypt-btn:disabled {
  background: #cbd5e0;
  cursor: not-allowed;
  box-shadow: none;
}

.encrypt-btn.active {
  background: linear-gradient(135deg, #3182ce 0%, #2c5282 100%);
}

.encrypt-icon {
  font-size: 1.2rem;
}

/* 结果区域 */
.result-section {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.result-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.result-label {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d3748;
}

.result-actions {
  display: flex;
  gap: 0.5rem;
}

.copy-btn:hover:not(:disabled) {
  color: #4299e1;
}

.verify-btn.active {
  background: #e6fffa;
  color: #319795;
}

.result-display {
  background: #f7fafc;
  border-radius: 8px;
  padding: 1.25rem;
  border: 1px solid #e2e8f0;
}

.hash-value {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d3748;
  word-break: break-all;
  line-height: 1.5;
  margin-bottom: 0.75rem;
}

.hash-meta {
  display: flex;
  justify-content: space-between;
  font-size: 0.875rem;
  color: #718096;
  padding-top: 0.75rem;
  border-top: 1px solid #e2e8f0;
}

/* 验证区域 */
.verify-section {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  animation: slideUp 0.3s ease;
}

.verify-header {
  margin-bottom: 1rem;
}

.verify-label {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d3748;
}

.verify-input-group {
  display: flex;
  gap: 0.75rem;
}

.verify-input {
  flex: 1;
  padding: 0.875rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  background: #fafafa;
}

.verify-input:focus {
  outline: none;
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
}

.verify-check-btn {
  padding: 0.875rem 1.5rem;
  background: #48bb78;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background 0.2s ease;
}

.verify-check-btn:hover:not(:disabled) {
  background: #38a169;
}

.verify-check-btn:disabled {
  background: #cbd5e0;
  cursor: not-allowed;
}

.verification-result {
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 8px;
  background: #f7fafc;
}

.verification-status {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.verification-status.valid {
  color: #38a169;
}

.verification-status.invalid {
  color: #e53e3e;
}

.status-icon {
  font-size: 1.2rem;
}

.verification-detail {
  font-size: 0.875rem;
  color: #718096;
  line-height: 1.5;
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 3rem 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.7;
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.75rem;
}

.empty-description {
  color: #718096;
  max-width: 500px;
  margin: 0 auto 1.5rem;
  line-height: 1.6;
}

.empty-action-btn {
  padding: 0.75rem 1.5rem;
  background: #4299e1;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.empty-action-btn:hover {
  background: #3182ce;
  transform: translateY(-1px);
}

/* 信息面板 */
.info-panel {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 350px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  z-index: 1000;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.info-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.info-panel-header h3 {
  margin: 0;
  font-size: 1.2rem;
  color: #2d3748;
}

.close-info-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #a0aec0;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.close-info-btn:hover {
  background: #f7fafc;
  color: #718096;
}

.info-panel-content {
  padding: 1.5rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f7fafc;
}

.info-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.info-item-title {
  font-weight: 600;
  color: #4a5568;
}

.info-item-value {
  color: #718096;
  text-align: right;
}

.info-item-value.warning {
  color: #e53e3e;
}

.info-note {
  margin-top: 1.25rem;
  padding: 0.875rem;
  background: #fffaf0;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #744210;
  border-left: 4px solid #ed8936;
}

/* 复制通知 */
.copy-notification {
  position: fixed;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  background: #38a169;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  animation: fadeInOut 2s ease;
}

@keyframes fadeInOut {
  0%, 100% { opacity: 0; transform: translate(-50%, 10px); }
  10%, 90% { opacity: 1; transform: translate(-50%, 0); }
}

.copy-icon {
  font-weight: bold;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .md5-encryptor {
    padding: 1.5rem 1rem;
  }
  
  .header {
    flex-direction: column;
    gap: 1rem;
  }
  
  .info-badge {
    align-self: flex-end;
  }
  
  .input-header,
  .result-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .input-actions,
  .result-actions {
    width: 100%;
    justify-content: flex-end;
  }
  
  .verify-input-group {
    flex-direction: column;
  }
  
  .verify-check-btn {
    width: 100%;
  }
  
  .info-panel {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: auto;
    border-radius: 12px 12px 0 0;
  }
  
  .title {
    font-size: 1.5rem;
  }
}
</style>